import { useSelector } from "react-redux";
import { getTenantIsInternal } from "store/userSlice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import { errorMessage, navHeight, scrollToTop } from "helpers/common";
import usePlyr from "hooks/usePlyr";
import sanitize from "helpers/sanitize";
import dayjs from "dayjs";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import { Input, InputAffix } from "components/ui";
import HtmlContent from "components/ui/HtmlContent";
import { useTranslation } from "translation/useTranslation";

function ArticleContent({ rawHtml }) {
  const isInternalTenant = useSelector(getTenantIsInternal);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);

  const { translate } = useTranslation();

  const handleHeadingClick = useCallback((id) => {
    if (!isInternalTenant || !id) return;
    navigate(`${location.pathname}${location.search}#${id}`);
    navigator.clipboard.writeText(id);
    toast.success(translate("help.anchor_copied"));
  }, [isInternalTenant, translate]);

  const sanitizedData = useMemo(() => {
    return sanitize(rawHtml, {
      ADD_TAGS: ["video", "source", "div", "figure"],
      ADD_ATTR: ["class", "poster", "playsinline", "controls", "preload", "src", "type"],
    });
  }, [rawHtml]);

  usePlyr(containerRef, [sanitizedData]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (event) => {
      const heading = event.target.closest("h4[id],h5[id],p[id]");
      if (heading) handleHeadingClick(heading.getAttribute("id"));

      const articleLink = event.target.closest('a[href^="/help?key="]');
      if (articleLink) {
        event.preventDefault();
        const startIndex = articleLink.href.indexOf("/help");
        navigate(articleLink.href.slice(startIndex));
      }
    };

    if (isInternalTenant) {
      container.querySelectorAll("h4[id],h5[id],p[id]").forEach(h => h.classList.add("heading-anchor"));
    }

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [sanitizedData, handleHeadingClick, isInternalTenant, navigate]);

  return <div ref={containerRef} className="help-page-content mt-3" dangerouslySetInnerHTML={{ __html: sanitizedData }} />;
}

export default function Index() {
  const { translate, editableTranslate } = useTranslation();

  const navigate = useNavigate();
  const { hash } = useLocation();


  const [searchParams, setSearchParams] = useSearchParams();
  const key = searchParams.get('key');

  const [error, setError] = useState(null);

  const [helpPages, setHelpPages] = useState([]);
  const [selectedParentPage, setSelectedParentPage] = useState(null);
  const [selectedHelpPage, setSelectedHelpPage] = useState(null);

  const [loading, setLoading] = useState(false);

  const [helpArticles, setHelpArticles] = useState([]);
  const [help, setHelp] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [searchArticles, setSearchArticles] = useState([]);

  const [helpMessages, setHelpMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const [selectedSort, setSelectedSort] = useState(null);

  const sortOptions = useMemo(() => {
    return [
      { icon: 'bi-sort-down-alt', label: translate('sorting.name_asc'), value: 'name-asc' },
      { icon: 'bi-sort-down', label: translate('sorting.name_desc'), value: 'name-desc' },
      { icon: 'bi-sort-down-alt', label: translate('sorting.creation_date_asc'), value: 'creation-asc' },
      { icon: 'bi-sort-down', label: translate('sorting.creation_date_desc'), value: 'creation-desc' },
    ];
  }, [translate]);

  useEffect(() => {
    fetchHelpMessages();
    fetchHelpPages();

    // detect hash and add highlight-anchor class
    const handleHashChange = () => {
      if (hash) {
        const id = hash.substring(1);
        const target = document.getElementById(id);
        if (target) {
          target.classList.add('highlight-anchor');
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (key) fetchHelp(key);
    else setHelp(null);
  }, [key]);

  useEffect(() => {
    if (selectedHelpPage) fetchHelpArticles();
    else setHelpArticles([]);
  }, [selectedHelpPage]);

  useEffect(() => {
    if (selectedSort && helpArticles.length > 0) sortHelpArticles(helpArticles);
  }, [selectedSort]);

  useEffect(() => {
    if (help) {
      const anchor = hash;
      if (anchor) {
        const id = anchor.replace('#', '');

        let attempts = 0;
        const maxAttempts = 20;
        const interval = setInterval(() => {
          const el = document.getElementById(id);
          if (el) {
            el.classList.add('highlight-anchor');
            const yOffset = -(navHeight() ?? 0);
            const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
            clearInterval(interval);
          } else {
            attempts++;
            if (attempts > maxAttempts) {
              clearInterval(interval);
            }
          }
        }, 100);
      }
    }
  }, [help, hash]);

  useEffect(() => {
    if (help && helpPages.length > 0) {
      if (help.related_sublevels && !help.related_sublevels[0]?.name) {
        const relatedSublevels = help.related_sublevels.map(related => {
          let found = null;
          for (const page of helpPages) {
            if (page?.children?.data && Array.isArray(page?.children?.data)) {
              for (const sub of page.children.data) {
                if (related.hash_id === sub.hash_id) {
                  found = sub;
                  break;
                }
              }
            }
            if (found) break;
          }

          if (found) return found;
          return related;
        });

        setHelp({
          ...help,
          related_sublevels: relatedSublevels
        });
      }
    }
  }, [help, helpPages]);

  const fetchHelpPages = async () => {
    try {
      const { data, status } = await api.get(`/help/pages?with_articles=true&has_long_text_only=true`);
      if (status === 200) {
        const arr = data.data.filter(d => {
          let hasArticles = false;
          for (const c of d?.children?.data) {
            if (c?.articles && Array.isArray(c.articles) && c.articles.length > 0) {
              hasArticles = true;
              break;
            }
          }
          return hasArticles;
        });
        sortArr(arr);

        for (let i = 0; i < arr.length; i++) {
          sortArr(arr[i].children.data);
        }

        setHelpPages(arr);
      }
    } catch (err) {
      setError(errorMessage(err));
      Sentry.captureException(err);
    }
  };

  const fetchHelpArticles = async () => {
    setLoading(true);
    setHelpArticles([]);
    scrollToTop();
    try {
      const { data, status } = await api.get(`/help/pages/${selectedHelpPage?.hash_id}?with_articles=true&has_long_text_only=true`);
      if (status === 200) {
        const arr = data.articles;

        if (arr) {
          sortHelpArticles(arr);
        }
      }
    } catch (err) {
      setError(errorMessage(err));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const fetchHelpMessages = async () => {
    try {
      const { data, status } = await api.get(`/help/messages`);
      if (status === 200) {
        const filtered = data.data.filter(m => m.key === 'welcome-message');
        setHelpMessages(filtered);

        if (!key) {
          handleMessageClick(null, filtered[0] ?? null)
        }
      }
    } catch (err) {
      setError(errorMessage(err));
      Sentry.captureException(err);
    }
  };

  const handlePageClick = (e, page, related = false) => {
    e.preventDefault();
    searchParams.delete('key');
    setSearchParams(searchParams);
    setSelectedHelpPage(page);

    if (related && page?.parent) {
      setSelectedParentPage(page.parent);
    }

    setHelp(null);
  };

  const handleParentPageClick = (e, page) => {
    e?.preventDefault();

    setSearchMode(false);
    setSearchInput('');

    setSelectedMessage(null);

    searchParams.delete('key');
    setSearchParams(searchParams);

    setSelectedParentPage(page);

    if (page?.children?.data && page?.children?.data?.length > 0) {
      setSelectedHelpPage(page?.children?.data[0]);
    } else {
      let found = false;
      for (const p of helpPages) {
        if (p.hash_id === page.hash_id) {
          if (p?.children?.data && p?.children?.data?.length > 0) {
            setSelectedHelpPage(p?.children?.data[0]);
            found = true;
            break;
          }
        }
      }
      if (!found) {
        setSelectedHelpPage(null);
      }
    }

    setHelp(null);
  };

  const handleArticleClick = (e, article, related = false) => {
    if (e && e?.target?.className === 'bi bi-pencil-square') return;
    else if (e) e.preventDefault();

    searchParams.set('key', article.hash_id);
    setSearchParams(searchParams);
  };

  const handleMessageClick = (e, message) => {
    e?.preventDefault();
    if (message) {
      setSearchMode(false);
      setSearchInput('');

      setSelectedParentPage(message);
      setSelectedHelpPage(null);
      setSelectedMessage(message);
    }
  };

  const fetchHelp = async (key) => {
    setSearchMode(false);
    setSearchInput('');
    setSelectedMessage(null);

    setLoading(true);
    scrollToTop();
    try {
      const { data, status } = await api.get(`/help/articles/${key}`);
      if (status === 200) {
        setHelp(data);
        if (selectedParentPage?.hash_id !== data?.page?.parent?.hash_id) {
          setSelectedParentPage(data.page?.parent);
        }
        if (selectedHelpPage?.hash_id !== data?.page?.hash_id) {
          setSelectedHelpPage(data.page);
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch help'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    setSearchArticles([]);

    if (!searchInput || searchInput.trim() === '') {
      if (helpMessages.length > 0) handleMessageClick(null, helpMessages[0]);
      else if (helpPages.length > 0) handleParentPageClick(null, helpPages[0]);
      return;
    }

    fetchSearchArticles();
    setSelectedParentPage(null);
    setSearchMode(true);
  };

  const fetchSearchArticles = async () => {
    setLoading(true);
    scrollToTop();
    try {
      const { data, status } = await api.get(`/help/articles?search=${encodeURIComponent(searchInput)}`);
      if (status === 200) {
        setSearchArticles(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch search articles'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const handleArticleSearchClick = (e, article) => {
    if (e && e?.target?.className === 'bi bi-pencil-square') return;
    else if (e) e.preventDefault();

    navigate('/help?key=' + article.hash_id);
  };

  const sortHelpArticles = (originalArr) => {
    let sortObj = sortOptions[0];

    if (!selectedSort) setSelectedSort(sortObj);
    else sortObj = selectedSort;

    const splitted = sortObj.value.split('-');
    const arr = structuredClone(originalArr);
    sortArr(arr, splitted[0], splitted[1]);
    setHelpArticles(arr);
  };

  const sortArr = (arr, key = 'name', direction = 'asc') => {
    if (key === null) return;

    arr.sort((a, b) => {
      let left = '';
      let right = '';

      if (key === 'name') {
        left = a.name.toLowerCase();
        right = b.name.toLowerCase();
      } else if (key === 'creation') {
        left = dayjs(a.content_created_at).unix();
        right = dayjs(b.content_created_at).unix();
      }

      if (direction === 'asc') {
        if (left < right) return -1;
        else if (left > right) return 1;
        return 0;
      } else {
        if (left < right) return 1;
        else if (left > right) return -1;
        return 0;
      }
    });
  };

  return <div className="row flex-nowrap">
    <div className="col-lg-auto col-md-3 section-col-left d-flex">
      <div className="bg-white w-100 p-4 content-card min-height-after-navbar">
        <ul className="sidebar-nav nav nav-pills flex-column sticky">
          {
            helpMessages.map((message, i) => <li key={i} className="nav-item">
              <a href="#" className={`nav-link ${selectedParentPage?.hash_id === message.hash_id ? 'active' : ''}`} onClick={e => handleMessageClick(e, message)} data-testid={`message-link-${message.hash_id}`}>{message.title}</a>
            </li>)
          }
          {
            helpPages.map((page, i) => {
              const isExpanded = selectedParentPage?.hash_id === page.hash_id && !selectedParentPage?.message;
              const isBoxed = isExpanded && page?.children?.data?.length > 0;
              return <li key={i} className="nav-item">
                <a href={`#${page.key}-nav`} className={`nav-link ${isBoxed ? 'has-children' : (isExpanded ? 'active' : '')}`} onClick={e => handleParentPageClick(e, page)} data-testid={`nav-item-${page.key}`}>{page.name} {isBoxed && <i className="bi bi-chevron-up"></i>}</a>

                <div className={`collapse ${isExpanded ? 'show' : ''}`} id={`${page.key}-nav`}>
                  <div>
                    {
                      page?.children?.data?.map((p, j) => <a key={j} href="#" className={selectedHelpPage?.hash_id === p.hash_id ? 'active' : ''} onClick={e => handlePageClick(e, p)} data-testid={`page-link-${p.hash_id}`}>{p.name}</a>)
                    }
                  </div>
                </div>
              </li>;
            })
          }
        </ul>
      </div>
    </div>
    <div className="col-lg col-md-9 section-col-right d-flex help-page-div">
      <div className={`bg-white w-100 p-4 content-card`}>
        <div className="d-flex justify-content-between">
          <h4>{editableTranslate('global.menu.help')}</h4>
          <form className="form-inline" onSubmit={handleSearch}>
            <InputAffix className="pl-0" prefix={
              <button type="submit" className="peb-input-affix__action peb-input-affix__action--compact" data-testid="search-button"><i className="bi bi-search"></i></button>
            }>
              <Input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} autoComplete="false" placeholder={translate('form.search')} maxLength={255} data-testid="search-input" />
            </InputAffix>
          </form>
        </div>

        <hr className="border-color-text" />

        {
          (!searchMode && selectedParentPage && selectedHelpPage) && <div className="mt-4" data-testid="help-page">
            {
              !help && <h5 className="text-big text-primary font-weight-bold">{selectedHelpPage.name}</h5>
            }

            {
              (!help && selectedHelpPage?.intro) && <HtmlContent
                html={selectedHelpPage?.intro}
                className="help-page-content mt-3"
                sanitizeOptions={{
                  ADD_TAGS: ["video", "source", "div", "figure"],
                  ADD_ATTR: ["class", "poster", "playsinline", "controls", "preload", "src", "type"],
                }}
              />
            }
          </div>
        }

        {
          error && <div className="pt-2">
            <ErrorAlert error={error} onClose={() => setError(null)} />
          </div>
        }

        {
          loading && <div className="mt-3" data-testid="loading">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="sr-only">Wird geladen...</span>
            </div>
          </div>
        }

        {
          searchMode ? <div className="div-bordered gray-box p-16px mt-4" data-testid="search-results">
            {
              !loading && <>
                <h6 className="font-weight-bold">{searchArticles.length} {editableTranslate('help.articles_found')}</h6>
                {
                  searchArticles.length > 0 && <ul className="help-ol mt-2 mb-0">
                    {
                      searchArticles.map((article, i) =>
                        <li key={i}>
                          <a href="#" onClick={e => handleArticleSearchClick(e, article)} data-testid={`article-link-${article.hash_id}`}>
                            {article.name}
                          </a>
                        </li>
                      )
                    }
                  </ul>
                }
              </>
            }
          </div> : <>
            {
              (!loading && !selectedMessage && help) && <div className="mt-4" data-testid="help-article">
                <h5 className="text-big text-primary font-weight-bold">{help.name}</h5>

                <ArticleContent rawHtml={help.long_text} />
                {
                  ((help.related_articles && help.related_articles.length > 0) || (help.related_sublevels && help.related_sublevels.length > 0)) && <div className="div-bordered gray-box p-16px mt-3" data-testid="related-articles">
                    <h6 className="font-weight-bold">{editableTranslate('help.related_articles')}</h6>
                    <ul className="help-ol mt-2 mb-0">
                      {
                        help.related_sublevels && help.related_sublevels.map((sub, i) =>
                          <li key={i}>
                            <a href="#" onClick={e => handlePageClick(e, sub, true)} data-testid={`page-link-${sub?.hash_id}`}>{sub?.name}</a>
                          </li>
                        )
                      }
                      {
                        help.related_articles && help.related_articles.map((article, i) =>
                          <li key={i}>
                            <a href="#" onClick={e => handleArticleClick(e, article, true)} data-testid={`article-link-${article.hash_id}`}>
                              {article.name}
                            </a>
                          </li>
                        )
                      }
                    </ul>
                  </div>
                }
              </div>
            }

            {
              selectedMessage && <div className="mt-4" data-testid="help-message">
                <h5 className="text-big text-primary font-weight-bold">{selectedMessage.title}</h5>

                <HtmlContent html={selectedMessage.message} className="help-page-content mt-3" sanitizeOptions={{
                  ADD_TAGS: ["video", "source", "div", "figure"],
                  ADD_ATTR: ["class", "poster", "playsinline", "controls", "preload", "src", "type"],
                }} />
              </div>
            }

            {
              (!loading && !selectedMessage && !key && !help && selectedHelpPage) && <div className="div-bordered gray-box p-16px mt-4" data-testid="available-articles">
                <h6 className="font-weight-bold">{editableTranslate('help.available_articles')}</h6>
                <ul className="help-ol mt-2 mb-0">
                  {
                    helpArticles.map((article, i) =>
                      <li key={i}>
                        <a href="#" onClick={e => handleArticleClick(e, article)} data-testid={`article-link-${article.hash_id}`}>
                          {article.name}
                        </a>
                      </li>
                    )
                  }
                </ul>
                {
                  helpArticles.length === 0 && <div data-testid="empty-state">{editableTranslate('global.no_data')}</div>
                }
              </div>
            }
          </>
        }
      </div>
    </div>
  </div>
}
